import { h, appendChildren, clear } from '@/lib/dom';

export interface ModalOptions {
  title: string;
  content: Node | ((close: () => void) => Node);
  size?: 'sm' | 'md' | 'lg';
  onClose?: () => void;
}

export interface ModalController {
  close: () => void;
  element: HTMLElement;
}

export function openModal(options: ModalOptions): ModalController {
  const body = document.body;
  const previouslyFocused = document.activeElement as HTMLElement | null;
  const backdrop = h('div', {
    class: 'modal-backdrop',
    role: 'presentation',
    on: {
      click: (ev) => {
        if (ev.target === backdrop) close();
      },
    },
  });
  const modal = h('div', {
    class: `modal modal-${options.size ?? 'md'}`,
    role: 'dialog',
    aria: { modal: 'true', labelledby: 'modal-title' },
    tabindex: '-1',
  });
  const titleEl = h('h2', { id: 'modal-title', class: 'modal-title' }, options.title);
  const closeBtn = h('button', {
    class: 'btn btn-ghost btn-sm',
    type: 'button',
    aria: { label: 'Закрыть' },
    on: { click: () => close() },
  }, '×');
  const header = h('div', { class: 'modal-header' }, [titleEl, closeBtn]);
  const bodyEl = h('div', { class: 'modal-body' });
  modal.append(header, bodyEl);
  const contentNode = typeof options.content === 'function' ? options.content(() => close()) : options.content;
  appendChildren(bodyEl, contentNode);
  backdrop.appendChild(modal);
  body.appendChild(backdrop);
  modal.focus();

  const FOCUSABLE_SELECTOR =
    'a[href], button, textarea, input, select, [tabindex]';

  // Layout-based visibility checks (offsetWidth/getClientRects) are always
  // zero in jsdom, which has no layout engine — walk the ancestor chain and
  // read computed style instead, which jsdom does resolve.
  function isVisible(el: HTMLElement): boolean {
    let node: HTMLElement | null = el;
    while (node) {
      if (node.hidden) return false;
      const style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      if (node === modal) break;
      node = node.parentElement;
    }
    return true;
  }

  function getFocusable(): HTMLElement[] {
    return Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) =>
        !el.hasAttribute('disabled') &&
        el.getAttribute('tabindex') !== '-1' &&
        isVisible(el),
    );
  }

  // Only the boundaries are intercepted; Tab between two elements in the
  // middle of the list is left to native browser focus navigation.
  function trapTab(ev: KeyboardEvent): void {
    const focusable = getFocusable();
    if (focusable.length === 0) {
      ev.preventDefault();
      modal.focus();
      return;
    }
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    const current = document.activeElement as HTMLElement | null;
    const idx = current ? focusable.indexOf(current) : -1;
    if (ev.shiftKey) {
      if (idx <= 0) {
        ev.preventDefault();
        last.focus();
      }
    } else {
      if (idx === -1 || idx === focusable.length - 1) {
        ev.preventDefault();
        first.focus();
      }
    }
  }

  function onKey(ev: KeyboardEvent): void {
    if (ev.key === 'Escape') {
      close();
    } else if (ev.key === 'Tab') {
      trapTab(ev);
    }
  }
  document.addEventListener('keydown', onKey);

  function close(): void {
    document.removeEventListener('keydown', onKey);
    clear(bodyEl);
    backdrop.remove();
    previouslyFocused?.focus();
    options.onClose?.();
  }

  return { close, element: backdrop };
}

export function confirmDialog(message: string, title = 'Подтвердите действие'): Promise<boolean> {
  return new Promise((resolve) => {
    const modal = openModal({
      title,
      size: 'sm',
      content: (close) =>
        h('div', null, [
          h('p', null, message),
          h('div', { class: 'modal-footer modal-footer-inline' }, [
            h('button', {
              class: 'btn btn-secondary',
              type: 'button',
              on: { click: () => { close(); resolve(false); } },
            }, 'Отмена'),
            h('button', {
              class: 'btn btn-primary',
              type: 'button',
              on: { click: () => { close(); resolve(true); } },
            }, 'Подтвердить'),
          ]),
        ]),
      onClose: () => resolve(false),
    });
    modal;
  });
}
